import {
  Body1Strong,
  Card,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Button,
} from '@fluentui/react-components';
import { Link as FluentLink } from '@fluentui/react-components';
import { OpenRegular } from '@fluentui/react-icons';
import { ControlledTooltip } from '@shared/components';
import { getItemPathByItemType } from '@modules/documents';
import getReferenceItemLabel from '@modules/documents/helpers/getReferenceItemLabel.helper';
import { TResolvedItemsMap } from '@modules/items';
import { IBaseDetectedElement, TDetectionsRowItem, TDetectionsRowVariant } from '../../../types/shared.types';
import { IDetectedReference, IDetectedSelfReference, isIDetectedSelfReference } from '../../../types/references.types';
import { IDetectedInlineBlock } from '../../../types/inlineBlocks.types';

const useStyles = makeStyles({
  card: { borderRadius: '12px', padding: 0 },
  table: {
    borderSpacing: '0 10px',
    '& tr': { height: '48px' },
  },
  tableBody: { '& > :last-child': { borderBottom: 'none' } },
});

type TProps<TDetectedElement extends IBaseDetectedElement> = {
  variant: TDetectionsRowVariant; // 'inline-blocks' | 'references'
  sourceId: string; // for links to source doc (row 3), resolvedItems lookup
  detectedElements: TDetectedElement[]; // already filtered to "inserted" by caller
  resolvedItems: TResolvedItemsMap;
};

const DetectionsSummaryTable = <TDetectedElement extends IBaseDetectedElement>({
  variant,
  sourceId,
  detectedElements,
  resolvedItems,
}: TProps<TDetectedElement>) => {
  const classes = useStyles();
  const src = resolvedItems[sourceId]?.item;
  if (!src || detectedElements.length === 0) return null;

  const rightHeader =
    variant === 'inline-blocks'
      ? 'Inserted inline block'
      : `Referenced ${isIDetectedSelfReference(detectedElements[0] as any) ? 'section' : 'document'}`;

  return (
    <Card className={classes.card}>
      <Table className={classes.table}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell className="!pl-5">
              <Body1Strong>Detected content</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell>
              <Body1Strong>{rightHeader}</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell className="!pr-5" style={{ width: '200px' }}>
              <Body1Strong>Inserted occurrence</Body1Strong>
            </TableHeaderCell>
          </TableRow>
        </TableHeader>

        <TableBody className={classes.tableBody}>
          {detectedElements.map((detectedElement) => {
            // left cell content
            const snippet = detectedElement.snippet;
            const elementId = detectedElement.elementId;

            // middle cell (target)
            let targetLabel: string;
            let targetLink: string | undefined;

            if (variant === 'inline-blocks') {
              const detectedInlineBlock = detectedElement as any as IDetectedInlineBlock;
              const inlineBlockId = detectedInlineBlock.chosenTargetInlineBlock as string;
              const ib = resolvedItems[inlineBlockId]?.item;
              targetLabel = ib?.name ?? 'Inline block';
              targetLink = ib ? `/${getItemPathByItemType(ib.type)}/${ib._id}` : undefined;
            } else {
              const detectedReference = detectedElement as any as IDetectedReference | IDetectedSelfReference;
              if (isIDetectedSelfReference(detectedReference)) {
                const headId = detectedReference.headingId;
                const headings = resolvedItems[sourceId]?.resolvedHeadings;
                const h = headings?.[headId];
                targetLabel = h ? `${h.numbering} ${h.text}` : 'Unknown heading';
                targetLink = `/${getItemPathByItemType(src.type)}/${src._id}?elem=${headId}`;
              } else {
                const selectedId = detectedReference.chosenTargetItem?.item;
                const item = selectedId ? resolvedItems[selectedId]?.item : undefined;
                targetLabel = item ? getReferenceItemLabel(item.name) : 'Unknown document';
                targetLink = item ? `/${getItemPathByItemType(item.type)}/${item._id}` : undefined;
              }
            }

            return (
              <TableRow key={detectedElement.id}>
                {/* Detected content */}
                <TableCell className="!pl-5">
                  <TableCellLayout truncate style={{ paddingTop: '3px', paddingBottom: '3px' }}>
                    <ControlledTooltip enable={snippet.length > 80} content={snippet} relationship="description">
                      <span>{snippet}</span>
                    </ControlledTooltip>
                  </TableCellLayout>
                </TableCell>

                {/* Target (inline block / section / document) */}
                <TableCell>
                  {targetLink ? (
                    <TableCellLayout truncate style={{ paddingTop: '3px', paddingBottom: '3px' }}>
                      <ControlledTooltip
                        enable={targetLabel.length > 80}
                        content={targetLabel}
                        relationship="description"
                      >
                        <FluentLink href={targetLink} target="_blank" onClick={(e) => e.stopPropagation()}>
                          {targetLabel}
                        </FluentLink>
                      </ControlledTooltip>
                    </TableCellLayout>
                  ) : (
                    targetLabel
                  )}
                </TableCell>

                {/* Inserted occurrence (link to source with highlight) */}
                <TableCell className="!pr-5">
                  <Button
                    icon={<OpenRegular />}
                    as="a"
                    href={`/${getItemPathByItemType(src.type)}/${src._id}?elem=${detectedElement.id}${
                      elementId ? `&highlight=true` : ''
                    }`}
                    target="_blank"
                    rel="noopener noreferrer"
                    appearance="primary"
                    size="small"
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
};

export default DetectionsSummaryTable;
